import React, {useEffect, useState} from 'react'
import styled from '@emotion/styled'
import {Toggle, Button, Radio} from '@thryvlabs/maverick'
import {useDispatch, useSelector} from 'react-redux'
import {
  removeItem,
  selectKP11SelectCartCentersNApps,
} from '../../../../../../app-store/kp11CartSlice'
import {
  CardPlanBodyStyle,
  CardPlanAddToCartStyle,
  CardPlanWebsiteUrlStyle,
} from '../kp-11Link-catalog-cards-style'
import {CardPlanEntitlementsStyle} from '../../../kp-11Link-reusable-components/kp-plan-ent-checkbox-style'
import {EntCheckboxInput} from '../../../kp-11Link-reusable-components/kp-plan-ent-checkbox'
import {MCCardWebsiteUrlComponent} from './kp-11Link-mc-card'
import {kpSuccessIcon} from '../../../../../../assets'
import {PlanRadioInput} from '../../../kp-11Link-reusable-components/kp-plan-radio-input'
import {CardPlanItemWrapStyle} from '../../../kp-11Link-reusable-components/kp-plan-radio-input-style'
import {DELAY_HIDE_IN_SEC} from '../../../../../../utils/constants'
import {
  buildCartEntity,
  getItemFromCart,
} from '../../../kp-11Link-utility/kp11-utility'

const OnboardingRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0 4px;
`

const OnboardingLabel = styled.span`
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #121212;
`

// The single plan shown in the UI — selecting website drives which code actually goes to cart
const SIGNATURE_PLUS_MONTHLY = 'signature_plus-5-thryv-m2m'
const SIGNATURE_PLUS_ANNUAL = 'signature_plus-5-thryv-annual'
// Fallback plan when "Professionally Designed Website" is unchecked
const FALLBACK_MONTHLY = 'mc_prov2-5-thryv-m2m'
const FALLBACK_ANNUAL = 'signature-5-thryv-annual'

const ANNUAL_CODES = [
  SIGNATURE_PLUS_ANNUAL,
  FALLBACK_ANNUAL,
  'premier-5-thryv-annual',
  'starter-5-thryv-annual',
]

const WEBSITE_ENT_CODE = 'ent-5-website'

export const KP11LinkThryvGrowthPlatformCardComponent = ({
  groupKey,
  items = [],
  onSelectItem,
  disabled,
  addedToCart,
  selectedCountry = 'US',
}) => {
  const dispatch = useDispatch()
  const cartCentersAndApps = useSelector(selectKP11SelectCartCentersNApps)
  const selectedItem = getItemFromCart({cartCentersAndApps, groupKey, items})

  // Billing toggle — annual plans are available when both variants exist
  const hasAnnual =
    items.some(i => i.code === SIGNATURE_PLUS_ANNUAL) &&
    items.some(i => i.code === FALLBACK_ANNUAL)

  const [isAnnual, setIsAnnual] = useState(false)

  // "Professionally Designed Website" checkbox controls the effective plan
  // true  → signature_plus (with website)
  // false → mc_prov2 / signature-annual (without website)
  const [isWebsiteChecked, setIsWebsiteChecked] = useState(true)

  // Non-website entitlements the user may toggle (only relevant when signature_plus is effective)
  const [otherAddons, setOtherAddons] = useState([])

  // Domain field state
  const [mcData, setMcData] = useState({
    mcDomain: selectedItem?.customFields?.mcDomain ?? '',
    isMcDomainValidated: false,
    isError: false,
    helperText: '',
    loading: false,
  })

  // Onboarding field state (null = not yet chosen, object { value: "Yes"/"No" } once chosen)
  const [includeOnboarding, setIncludeOnboarding] = useState(
    selectedItem?.customFields?.includeOnboarding ?? null,
  )

  const handleIncludeOnboardingChange = ({target: {value}}) => {
    setIncludeOnboarding(prevState => ({...prevState, value}))
  }
  const selectedOnboarding = includeOnboarding?.value ?? includeOnboarding

  // Derive the display item (signature_plus for the current billing type)
  const displayItem = items.find(
    i => i.code === (isAnnual ? SIGNATURE_PLUS_ANNUAL : SIGNATURE_PLUS_MONTHLY),
  )

  // Derive the fallback item when website is unchecked
  const fallbackItem = items.find(
    i => i.code === (isAnnual ? FALLBACK_ANNUAL : FALLBACK_MONTHLY),
  )

  // The item that will actually be added to the cart
  const effectiveItem = isWebsiteChecked ? displayItem : fallbackItem

  // Website entitlement always comes from signature_plus so the toggle checkbox is always visible
  const websiteEntitlement = displayItem?.entitlements?.find(
    e => e.code === WEBSITE_ENT_CODE,
  )
  // Other entitlements come from whichever plan is currently effective:
  // - signature_plus (website checked): its non-website entitlements
  // - fallback plan (website unchecked): its own entitlements (e.g. ent-5-reviewack, ent-5-gmb)
  const otherEntitlements =
    effectiveItem?.entitlements?.filter(e => e.code !== WEBSITE_ENT_CODE) ?? []

  // Reset website checkbox to checked when billing type is toggled (fresh plan selection)
  useEffect(() => {
    setIsWebsiteChecked(true)
    setOtherAddons([])
  }, [isAnnual])

  // Reset other addons when switching between signature_plus and fallback (entitlement sets differ)
  useEffect(() => {
    setOtherAddons([])
  }, [isWebsiteChecked])

  // Sync state from an existing cart item on mount
  useEffect(() => {
    if (!selectedItem?.code) return
    const isPlus = [SIGNATURE_PLUS_MONTHLY, SIGNATURE_PLUS_ANNUAL].includes(
      selectedItem.code,
    )
    setIsWebsiteChecked(isPlus)
    setIsAnnual(ANNUAL_CODES.includes(selectedItem.code))
    setMcData(prev => ({
      ...prev,
      mcDomain: selectedItem?.customFields?.mcDomain ?? '',
      isMcDomainValidated: false,
    }))
    setIncludeOnboarding(selectedItem?.customFields?.includeOnboarding ?? null)
    // Restore non-website addons from cart
    const restoredOther = (selectedItem?.addons ?? []).filter(
      a => a.code !== WEBSITE_ENT_CODE,
    )
    setOtherAddons(restoredOther)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOtherAddon = ent => {
    setOtherAddons(prev => {
      const exists = prev.some(a => a.code === ent.code)
      if (exists) return prev.filter(a => a.code !== ent.code)
      return [
        ...prev,
        buildCartEntity({kind: 'addon', source: ent, selectedCountry}),
      ]
    })
  }

  const isAddToCartDisabled = disabled || !effectiveItem

  const handleAddToCart = () => {
    if (!effectiveItem) return
    if (selectedItem?.code) {
      dispatch(removeItem({groupKey}))
    }
    const updatedItem = buildCartEntity({
      kind: 'item',
      source: {...effectiveItem, groupKey, section: 'centers'},
      selectedCountry,
      quantity: 1,
      customFields: {
        mcDomain: mcData.mcDomain,
        includeOnboarding,
      },
    })
    const addons = []
    if (isWebsiteChecked && websiteEntitlement) {
      addons.push(
        buildCartEntity({
          kind: 'addon',
          source: websiteEntitlement,
          selectedCountry,
        }),
      )
    }
    // Always include other addons — applies to both signature_plus and fallback plan
    addons.push(...otherAddons)
    updatedItem.addons = addons
    onSelectItem(updatedItem)
  }

  const [add2CartBannerStatus, setAdd2BannerStatus] = useState(addedToCart)
  useEffect(() => {
    if (addedToCart) {
      setAdd2BannerStatus(true)
      setTimeout(() => setAdd2BannerStatus(false), DELAY_HIDE_IN_SEC * 1000)
    }
  }, [addedToCart])

  return (
    <CardPlanBodyStyle>
      <CardPlanItemWrapStyle wrap={true} column={true}>
        {hasAnnual && (
          <div style={{marginBottom: '12px', paddingLeft: '8px'}}>
            <Toggle
              label="Annual"
              toggleSize="normal"
              onChange={setIsAnnual}
              toggled={isAnnual}
              customSelectedColor="#16A085"
              customUnselectedColor="#808080"
            />
          </div>
        )}

        <div className="card-plan-subText">Select a plan</div>

        {displayItem && (
          <PlanRadioInput
            key={displayItem.code}
            item={displayItem}
            checked={true}
            disabled={disabled || displayItem.isAlreadyAdded}
            onChange={() => {}}
            country={selectedCountry}
          />
        )}

        {/* Entitlements — website checkbox drives the effective plan code */}
        {(websiteEntitlement || otherEntitlements.length > 0) && (
          <CardPlanEntitlementsStyle>
            {websiteEntitlement && (
              <EntCheckboxInput
                key={WEBSITE_ENT_CODE}
                label={websiteEntitlement.name}
                checked={isWebsiteChecked}
                disabled={disabled || websiteEntitlement.isAlreadyAdded}
                onChange={() => {
                  if (disabled || websiteEntitlement.isAlreadyAdded) return
                  setIsWebsiteChecked(prev => !prev)
                }}
              />
            )}
            {otherEntitlements.map(ent => (
              <EntCheckboxInput
                key={ent.code}
                label={ent.name}
                checked={otherAddons.some(a => a.code === ent.code)}
                disabled={disabled || ent.isAlreadyAdded || ent.disabled}
                onChange={() => {
                  if (disabled || ent.isAlreadyAdded || ent.disabled) return
                  toggleOtherAddon(ent)
                }}
              />
            ))}
          </CardPlanEntitlementsStyle>
        )}
      </CardPlanItemWrapStyle>

      {/* Domain field */}
      <CardPlanWebsiteUrlStyle>
        <MCCardWebsiteUrlComponent
          mcData={mcData}
          setMcData={partial => setMcData(prev => ({...prev, ...partial}))}
          required={false}
        />
      </CardPlanWebsiteUrlStyle>

      {/* Onboarding field */}
      <OnboardingRow>
        <OnboardingLabel>Do you want to add onboarding?</OnboardingLabel>
        <div className="d-flex gap-3">
          <Radio
            hasLabel
            id={`tgp-onboarding-yes-${groupKey}`}
            name={`tgp-onboarding-${groupKey}`}
            value="Yes"
            label="Yes"
            toggled={selectedOnboarding === 'Yes'}
            handleOnChange={handleIncludeOnboardingChange}
            disabled={disabled}
          />
          <Radio
            hasLabel
            id={`tgp-onboarding-no-${groupKey}`}
            name={`tgp-onboarding-${groupKey}`}
            value="No"
            label="No"
            toggled={selectedOnboarding === 'No'}
            handleOnChange={handleIncludeOnboardingChange}
            disabled={disabled}
          />
        </div>
      </OnboardingRow>

      <div
        className="d-flex w-100 justify-content-center"
        style={{marginTop: '16px'}}
      >
        <Button
          variant="primary"
          disabled={isAddToCartDisabled}
          onClick={handleAddToCart}
        >
          Add to Cart
        </Button>
      </div>

      {add2CartBannerStatus && (
        <CardPlanAddToCartStyle>
          <img src={kpSuccessIcon} alt="success icon" />
          <span>Added to cart</span>
        </CardPlanAddToCartStyle>
      )}
    </CardPlanBodyStyle>
  )
}
